import React, { useRef } from 'react';
import { ChevronRight, Crown, Medal, Gift, Coins } from 'lucide-react';
import { Link } from 'react-router-dom';
import RailCard from './RailCard';

/**
 * One horizontal-scrolling Discover rail.
 *
 * Pure presentation — accepts the rail object as returned by
 * `/api/discover/feed`:
 *   { id, title, subtitle?, items: [...], kind?: 'continue'|'authors' }
 *
 * Snap-scrolling on mobile, native trackpad scroll on desktop. Hides
 * itself when items is empty so the page composes cleanly from whatever
 * the API returns without empty-state plumbing.
 */
const Rail = ({ rail, onSeeAll, savedSet, onSaveChange, animationDelay }) => {
  const scrollerRef = useRef(null);
  if (!rail?.items || rail.items.length === 0) return null;

  const cardSize = rail.kind === 'continue' ? 'lg' : 'md';

  // A subtle 4px coloured stripe to the left of the rail title gives each
  // rail a visual identity at a glance — without spending real estate.
  const accent = (() => {
    if (rail.id === 'featured') return 'bg-amber-400';
    if (rail.id === 'continue') return 'bg-amber-400';
    if (rail.id === 'top_plays') return 'bg-rose-500';
    if (rail.id === 'top_completed') return 'bg-emerald-400';
    if (rail.id === 'because_you_listened') return 'bg-fuchsia-400';
    if (rail.id === 'new_arrivals') return 'bg-sky-400';
    if (rail.id === 'trending_authors') return 'bg-purple-400';
    if (rail.id?.startsWith('genre:')) return 'bg-indigo-400';
    if (rail.id === 'mood:cosy') return 'bg-amber-500';
    if (rail.id === 'mood:edge') return 'bg-red-500';
    if (rail.id === 'mood:dreamy') return 'bg-indigo-500';
    if (rail.id === 'mood:heartbreak') return 'bg-rose-500';
    if (rail.id === 'featured_gifts') return 'bg-rose-400';
    if (rail.id === 'top_gifters') return 'bg-amber-400';
    return 'bg-slate-500';
  })();

  // Featured Gifts → render shop-item cards inline (link to /shop).
  if (rail.id === 'featured_gifts') {
    return (
      <section
        className="space-y-3 opacity-0 animate-[fadeUp_0.5s_ease-out_forwards]"
        style={animationDelay ? { animationDelay: `${animationDelay}ms` } : undefined}
        data-testid={`rail-${rail.id}`}
      >
        <div className="flex items-end justify-between gap-2 px-4 sm:px-6">
          <div className="min-w-0 flex items-stretch gap-3">
            <span className={`w-1 rounded-full ${accent}`} aria-hidden="true" />
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-semibold text-white tracking-tight flex items-center gap-2">
                <Gift className="w-5 h-5 text-rose-400" /> {rail.title}
              </h2>
              {rail.subtitle && <p className="text-xs text-rose-300/80 mt-0.5 truncate">{rail.subtitle}</p>}
            </div>
          </div>
          <Link
            to="/shop"
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-rose-300 transition-colors px-2 py-1 font-semibold tracking-wider"
            data-testid={`rail-${rail.id}-shop`}
          >
            OPEN SHOP <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="flex gap-3 sm:gap-4 overflow-x-auto px-4 sm:px-6 pb-2 snap-x snap-mandatory scrollbar-thin">
          {rail.items.map((item) => (
            <Link
              key={item.shop_item_id || item.book_id}
              to="/shop"
              className="snap-start shrink-0 w-44 rounded-xl bg-slate-800/60 border border-slate-700 hover:border-rose-500/60 transition-all hover:-translate-y-0.5 hover:shadow-lg overflow-hidden"
              data-testid={`gift-rail-card-${item.shop_item_id || item.book_id}`}
            >
              <div className="relative aspect-square bg-gradient-to-br from-rose-500/30 via-amber-500/20 to-slate-900 flex items-center justify-center">
                <Gift className="w-12 h-12 text-rose-200/80" />
                {item.gift_count > 0 && (
                  <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-100 bg-rose-500/85 px-1.5 py-0.5 rounded-full">
                    {item.gift_count}× gifted
                  </span>
                )}
              </div>
              <div className="p-3">
                <p className="text-sm font-bold text-white truncate">{item.title}</p>
                <p className="text-[11px] text-slate-400 mt-0.5 flex items-center justify-between">
                  <span className="capitalize">{item.shop_category}</span>
                  {item.shop_price_coins > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-amber-200 font-semibold">
                      <Coins className="w-3 h-3" /> {item.shop_price_coins}
                    </span>
                  )}
                </p>
              </div>
            </Link>
          ))}
          <div className="flex-shrink-0 w-2" aria-hidden="true" />
        </div>
      </section>
    );
  }

  // Top Gifters leaderboard — vertical list (compact), not a horizontal rail.
  if (rail.id === 'top_gifters') {
    return (
      <section
        className="space-y-3 opacity-0 animate-[fadeUp_0.5s_ease-out_forwards] px-4 sm:px-6"
        style={animationDelay ? { animationDelay: `${animationDelay}ms` } : undefined}
        data-testid={`rail-${rail.id}`}
      >
        <div className="flex items-end justify-between gap-2">
          <div className="min-w-0 flex items-stretch gap-3">
            <span className={`w-1 rounded-full ${accent}`} aria-hidden="true" />
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-semibold text-white tracking-tight flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-400" /> {rail.title}
              </h2>
              {rail.subtitle && <p className="text-xs text-amber-300/80 mt-0.5">{rail.subtitle}</p>}
            </div>
          </div>
        </div>
        <ol className="rounded-xl bg-slate-800/60 border border-slate-700 divide-y divide-slate-700/60 overflow-hidden">
          {rail.items.map((g, i) => {
            const rank = i + 1;
            const RankIcon = rank === 1 ? Crown : rank <= 3 ? Medal : null;
            const tone = rank === 1 ? 'text-amber-300' : rank === 2 ? 'text-slate-300' : rank === 3 ? 'text-orange-400' : 'text-slate-500';
            return (
              <li key={g.user_id} className="flex items-center gap-3 p-3" data-testid={`gifter-row-${rank}`}>
                <div className={`flex items-center justify-center w-7 h-7 shrink-0 ${tone}`}>
                  {RankIcon ? <RankIcon className="w-5 h-5" /> : <span className="font-bold text-xs">{rank}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{g.display_name}</p>
                  <p className="text-[11px] text-slate-400">
                    {g.gift_count} gift{g.gift_count === 1 ? '' : 's'} sent
                  </p>
                </div>
                <span className="inline-flex items-center gap-1 text-sm font-bold text-amber-200">
                  <Coins className="w-3.5 h-3.5" /> {g.coins_spent}
                </span>
              </li>
            );
          })}
        </ol>
      </section>
    );
  }

  return (
    <section
      className="space-y-3 opacity-0 animate-[fadeUp_0.5s_ease-out_forwards]"
      style={animationDelay ? { animationDelay: `${animationDelay}ms` } : undefined}
      data-testid={`rail-${rail.id}`}
    >
      <div className="flex items-end justify-between gap-2 px-4 sm:px-6">
        <div className="min-w-0 flex items-stretch gap-3">
          <span className={`w-1 rounded-full ${accent}`} aria-hidden="true" />
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-semibold text-white tracking-tight">
              {rail.title}
            </h2>
            {rail.subtitle && (
              <p className="text-xs text-amber-300/80 mt-0.5 truncate">
                {rail.subtitle}
              </p>
            )}
          </div>
        </div>
        {onSeeAll && (
          <button
            onClick={() => onSeeAll(rail)}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-amber-300 transition-colors px-2 py-1 font-semibold tracking-wider"
            data-testid={`rail-${rail.id}-see-all`}
          >
            VIEW ALL
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-3 sm:gap-4 overflow-x-auto px-4 sm:px-6 pb-2 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent"
        style={{ scrollbarWidth: 'thin' }}
      >
        {rail.items.map((item, idx) => (
          <div
            key={item.book_id || item.series_id || item.author || idx}
            className="snap-start"
          >
            <RailCard
              item={item}
              kind={rail.kind}
              size={cardSize}
              savedSet={savedSet}
              onSaveChange={onSaveChange}
            />
          </div>
        ))}
        {/* Trailing spacer so the last card can fully reveal on scroll. */}
        <div className="flex-shrink-0 w-2" aria-hidden="true" />
      </div>
    </section>
  );
};

export default Rail;
